// @flow

import React from 'react';
import _ from 'lodash';
import gql from 'graphql-tag';
import memoize from 'memoize-weak';
import { connect } from 'react-redux';

import { Query } from '@apollo/react-components';

import * as Profile from 'app/entities/Profile';
import * as Zap from 'app/entities/Zap';
import ErrorBlockOrComponent from 'app/common/components/ErrorBlockOrComponent';
import FilterableZapsList from 'app/zap-mover/components/FilterableZapsList/FilterableZapsList';
import LoadingBlockOrComponent from 'app/common/components/LoadingBlockOrComponent';
import hocify from 'app/common/components/hocify';
import { homeFolder } from 'app/dashboard/utils/constants';

import type { Dispatch } from 'app/common/types';
import type { FolderType } from 'app/dashboard/types';

const zapToProps = memoize(zapEntity => ({
  folderId: Zap.selectors.folderId(zapEntity),
  id: Zap.selectors.id(zapEntity),
  isOn: Zap.selectors.isOn(zapEntity),
  isSelected: Zap.selectors.isSelectedForMoving(zapEntity),
  services: Zap.selectors.nodeApis(zapEntity),
  title: Zap.selectors.title(zapEntity) || 'Unnamed Zap',
}));

const folderPropsWithZaps = zapProps => folderProps => ({
  ...folderProps,
  zaps: zapProps.filter(({ folderId }) => folderId === folderProps.id),
});

type Props = {
  fromAccountId: number,
  includeDrafts?: boolean,
  includeShared?: boolean,
  isSingleSelect?: boolean,
};

type OwnProps = {
  ...$Exact<Props>,
  folders: FolderType[],
  isLoading: boolean,
};

const mapStateToProps = (
  state,
  {
    folders = [],
    isLoading,
    fromAccountId,
    includeDrafts,
    includeShared,
  }: OwnProps
) => {
  const currentProfileId = Profile.selectors.all.currentProfileId(state);
  const query = Zap.selectors.all.movingZapsQuery(state);
  const zaps = Zap.selectors.all
    .inAccount(fromAccountId, state)
    .filter(
      includeShared
        ? _.stubTrue
        : _.partial(Zap.selectors.isOwnedBy, currentProfileId)
    )
    .filter(includeDrafts ? _.stubTrue : _.negate(Zap.selectors.isDraft));
  const zapsAsProps = zaps
    .filter(_.partial(Zap.selectors.titleMatches, query))
    .map(_.ary(zapToProps, 1));
  const foldersAsProps = folders
    .reverse()
    .concat(homeFolder)
    .reverse()
    .map(folderPropsWithZaps(zapsAsProps))
    .filter(folderProps => folderProps.zaps.length > 0);

  return {
    folders: foldersAsProps,
    fromAccountId,
    isLoading: isLoading || Zap.selectors.all.isLoadImpending(state),
    query,
    shouldLoad: (hasMounted, { prevProps, nextProps }) => {
      return (
        !!nextProps.fromAccountId &&
        (!hasMounted || nextProps.fromAccountId !== prevProps.fromAccountId)
      );
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    onChooseFolder(isSelected, id) {
      dispatch(
        Zap.actions.selectFolderForMoving(
          isSelected,
          Number(id),
          ownProps.includeDrafts
        )
      );
    },
    onChooseZap(isSelected, id) {
      if (ownProps.isSingleSelect) {
        dispatch(Zap.actions.deselectAllZapsForMoving());
      }
      dispatch(Zap.actions.selectZapsForMoving(isSelected, id));
    },
    onFilterZaps(query) {
      dispatch(Zap.actions.updateMovingZapsQuery(query));
    },
    onLoad({ fromAccountId }) {
      const params = { account_id: fromAccountId };
      dispatch(Zap.actions.loadCollectionWithParams(params));
    },
  };
};

const FilterableZapsListContainer = _.flowRight(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  hocify(ErrorBlockOrComponent),
  hocify(LoadingBlockOrComponent)
)(FilterableZapsList);

const foldersQuery = gql`
  query ZapMoverFolders($accountId: Int!) {
    account(id: $accountId) {
      id
      folders {
        id
        isShared
        title
      }
    }
  }
`;

const QueriedFilterableZapsListContainer = (props: Props) => {
  return (
    <Query query={foldersQuery} variables={{ accountId: props.fromAccountId }}>
      {({ data = {}, error, loading }) => (
        <FilterableZapsListContainer
          {...props}
          errorMessage={error && error.message}
          folders={_.get(data, ['account', 'folders'], [])}
          isLoading={loading}
        />
      )}
    </Query>
  );
};

export default QueriedFilterableZapsListContainer;
